import React, { ReactNode } from 'react';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import styled from 'styled-components';

import { ParsedArticle } from 'lib/contentful/shapes';

const H2 = 'heading-2';
const H3 = 'heading-3';

/**
 * Extract the h2 and h3 heading structure of a post.
 */

interface HeadingLevel2 {
  id: string;
  level: 2;
  text: string;
  children?: HeadingLevel3[];
}

interface HeadingLevel3 {
  id: string;
  level: 3;
  text: string;
}

export type MappedHeadings = HeadingLevel2[];

export const mapPostIntoHeadingTree = (post: ParsedArticle) => {
  const headingNodes = post.body.content.filter((node) =>
    [H2, H3].includes(node.nodeType)
  );

  const flatHeadings = headingNodes.map((node) => ({
    level: getNumberFromString(node.nodeType),
    text: getTextFromHeadingNode(node) || '',
    id: getIdFromHeadingNode(node),
  }));

  const headingTree: MappedHeadings = [];

  flatHeadings.forEach(({ level, text, id }, index) => {
    if (level === 2) {
      const remainingItems = flatHeadings.slice(index + 1);
      const indexOfNextMainHeading = remainingItems.findIndex(
        (node) => node.level === 2
      );

      const children = remainingItems.slice(
        0,
        indexOfNextMainHeading
      ) as HeadingLevel3[];

      headingTree.push({ level: 2, text, children, id });
    }
  });

  return headingTree;
};

/**
 * `renderNode` options for the `documentToReactComponents` function.
 * It adds ids to h2 and h3 headings, so they can be anchored to
 *
 * Example usage:
 * ```jsx
 * return documentToReactComponents(document, {renderNode: anchorHeaderNodeOptions})
 * ```
 *
 */
export const anchorableHeaderOptions = {
  renderNode: {
    // eslint-disable-next-line react/display-name
    [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
      return (
        <HeadingWithAnchorOffset as="h2" id={getIdFromHeadingNode(node)}>
          {children}
        </HeadingWithAnchorOffset>
      );
    },
    // eslint-disable-next-line react/display-name
    [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
      return (
        <HeadingWithAnchorOffset as="h3" id={getIdFromHeadingNode(node)}>
          {children}
        </HeadingWithAnchorOffset>
      );
    },
  },
};

/**
 * Approximate height of the header on the top of the page
 */
const APPROX_HEADER_OFFSET = '110px';

const HeadingWithAnchorOffset = styled.h2`
  scroll-margin-top: ${APPROX_HEADER_OFFSET};
`;

function getNumberFromString(str: string) {
  return parseInt(str.replace(/^\D+/g, ''));
}

function getTextFromHeadingNode(node: Block | Inline) {
  const content = node.content?.[0];
  if ('value' in content) {
    return content.value;
  }
  return '';
}

function getIdFromHeadingNode(node: Block | Inline) {
  const text = getTextFromHeadingNode(node);

  return text
    .trim()
    .replace(/\s/g, '_') // replace white space with underscore
    .replace(/\W+/g, '') // remove special characters
    .toLowerCase();
}
